import { forwardRef, type ButtonHTMLAttributes, type MouseEvent, type ReactNode } from 'react';
import type { CarbonIconType } from '@carbon/icons-react';
import { cx } from '@/shared/lib/cx';
import s from './Button.module.css';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'danger' | 'danger-ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Иконка Carbon (16px). */
  icon?: CarbonIconType;
  iconPosition?: 'start' | 'end';
  /** Идёт действие: кнопка остаётся в фокусе, но не нажимается повторно (защита от двойной отправки). */
  loading?: boolean;
  fullWidth?: boolean;
  /** Разрешить перенос длинной подписи (например, на казахском) вместо обрезки. */
  wrap?: boolean;
  children?: ReactNode;
}

const VARIANT_CLASS: Record<ButtonVariant, string | undefined> = {
  primary: s.primary,
  secondary: s.secondary,
  tertiary: s.tertiary,
  ghost: s.ghost,
  danger: s.danger,
  'danger-ghost': s.dangerGhost,
};

export function buttonClassName(options: { variant?: ButtonVariant; size?: ButtonSize; fullWidth?: boolean; wrap?: boolean; className?: string }): string {
  const { variant = 'primary', size = 'md', fullWidth, wrap, className } = options;
  return cx(s.button, VARIANT_CLASS[variant], size !== 'md' && s[size], fullWidth && s.fullWidth, wrap && s.wrap, className);
}

/** Кнопка действия. Одна основная (primary) кнопка на экран или группу. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', icon: Icon, iconPosition = 'start', loading = false, fullWidth, wrap, className, children, type, onClick, disabled, ...rest },
  ref,
) {
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (loading) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };
  const iconNode = Icon ? <Icon size={16} aria-hidden="true" /> : null;
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={buttonClassName({ variant, size, fullWidth, wrap, className })}
      disabled={disabled}
      aria-disabled={loading || undefined}
      aria-busy={loading || undefined}
      onClick={handleClick}
      {...rest}
    >
      {loading ? <Spinner size="sm" decorative /> : iconPosition === 'start' && iconNode}
      <span className={s.label}>{children}</span>
      {!loading && iconPosition === 'end' && iconNode}
    </button>
  );
});
